import * as React from "react"
import { cn } from "~/lib/utils"

interface LoadingShimmerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional custom loading text. Defaults to the standard loading message. */
  text?: string
  /** Optional array of texts to cycle through. Will stop on the last one. */
  texts?: string[]
}

export const LoadingShimmer = React.forwardRef<
  HTMLDivElement,
  LoadingShimmerProps
>(({ className, text, texts, ...props }, ref) => {
  const defaultText = i18n.t("js.multi_domain_search.loading")
  const [currentIndex, setCurrentIndex] = React.useState(0)

  // Determine which texts to use
  const textsToUse = texts && texts.length > 0 ? texts : [text || defaultText]
  const isArray = texts && texts.length > 0
  const currentText = textsToUse[currentIndex]

  React.useEffect(() => {
    if (!isArray || textsToUse.length <= 1) {
      return
    }

    // Stop on the last text
    if (currentIndex >= textsToUse.length - 1) {
      return
    }

    // Fixed 5 second interval between text switches
    const timeoutId = setTimeout(() => {
      setCurrentIndex((prev) => prev + 1)
    }, 5000)

    return () => clearTimeout(timeoutId)
  }, [currentIndex, isArray, textsToUse.length])

  return (
    <div
      ref={ref}
      className={cn(
        "d-flex justify-content-center align-items-center",
        className,
      )}
      data-testid="loading-shimmer"
      {...props}
    >
      <span className="text-shimmer">{currentText}</span>
    </div>
  )
})
LoadingShimmer.displayName = "LoadingShimmer"
